import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useApi } from '../../hooks/useApi';
import { useCompanies } from '../../features/company/CompanyContext';
import { Card, Loading, ModuleHero, Notice, Spinner, StatusPill } from '../../components/ui';
import NoCompany from '../../components/NoCompany';
import { errorMessage, openProtectedFile } from '../../lib/http';
import { formatDateTime, gradeTone } from '../../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE } from '../../lib/ui';
import type { Company, ReadinessBrief } from '../../types/workspace';

type MajorConcern = {
  id: string;
  title: string;
  detail: string;
  mitigation: string;
  documentCode: string | null;
  severity: string;
  changeable: boolean;
};

type PartnerDoc = { code: string; title: string; purpose: string; required: boolean };

type Response = {
  company: Company;
  brief: ReadinessBrief | null;
  locked: boolean;
  hasRating: boolean;
  report: { version: number; grade: string; score: number; outlook: string | null; summary: string } | null;
  openActions: number;
  majorConcerns: MajorConcern[];
  partnerRequiredDocs: PartnerDoc[];
};

function Fact({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{label}</dt>
      <dd className="mt-0.5 text-sm text-[#4a1a30]">{value || '—'}</dd>
    </div>
  );
}

function SignOff({ label, at, by, pendingText }: { label: string; at: string | null | undefined; by?: { name: string | null; email: string } | null; pendingText: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl px-3 py-2" style={{ background: at ? '#ecfdf5' : '#fff', border: `1px solid ${at ? '#a7f3d0' : '#fce7f3'}` }}>
      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-black ${at ? 'bg-emerald-500 text-white' : 'bg-[#fdf2f8] text-[#c084a0]'}`}>{at ? '✓' : '·'}</span>
      <div className="min-w-0">
        <p className="text-xs font-bold text-[#6b0030]">{label}</p>
        <p className="truncate text-[11px] text-[#b0708f]">{at ? `${by?.name || by?.email || 'Signed'} · ${formatDateTime(at)}` : pendingText}</p>
      </div>
    </div>
  );
}

function Block({ label, text }: { label: string; text?: string | null }) {
  if (!text?.trim()) return null;
  return (
    <div className="mt-4 border-t border-[#fce7f3] pt-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{label}</p>
      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-[#4a1a30]">{text}</p>
    </div>
  );
}

export default function Ready() {
  const { data, loading, error, code, reload } = useApi<Response>('/customer/ready');
  const { refresh } = useCompanies();
  const [busy, setBusy] = useState<'confirm' | 'pdf' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'green' | 'rose' | 'amber' | 'pink'; text: string } | null>(null);

  if (loading) return <Loading />;
  if (code === 'NO_COMPANY') return <NoCompany />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  const c = data.company;
  const brief = data.brief;
  const ready = Boolean(brief?.customerConfirmedAt);
  const hasOverview = Boolean(brief?.overview?.trim());
  const blockedOutreach = ['OUTREACH', 'FUNDED'].includes(c.status);
  const canConfirm = !data.locked && data.hasRating && hasOverview && !ready && !blockedOutreach;
  const confirmBlockReason = ready
    ? null
    : blockedOutreach
      ? 'Partners have already been contacted. Your analyst manages outreach in Consult.'
      : !data.hasRating
        ? 'A published rating is required before you can confirm.'
        : data.locked
          ? 'Pay the rating fee on Rated before confirming.'
          : !hasOverview
            ? 'Your Micus analyst is still drafting the Ready summary in Consult.'
            : null;
  const majorConcerns = data.majorConcerns || [];
  const partnerDocs = data.partnerRequiredDocs || [];

  const confirm = async () => {
    setBusy('confirm');
    setNotice(null);
    try {
      const { data: res } = await api.post('/customer/ready/confirm');
      setNotice({
        tone: 'green',
        text: res.ready
          ? 'Approved. Your Micus analyst will send this Ready pack to funding partners from Consult.'
          : 'Confirmed. Your Micus analyst will be notified.',
      });
      await Promise.all([reload(true), refresh()]);
    } catch (err) {
      setNotice({ tone: 'rose', text: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  const openPdf = async () => {
    setBusy('pdf');
    setNotice(null);
    try {
      await openProtectedFile('/customer/ready/pdf');
    } catch {
      setNotice({ tone: 'rose', text: 'Could not open the Ready pack PDF.' });
    } finally {
      setBusy(null);
    }
  };

  const address = [c.addressLine, c.addressLine2, c.city, c.stateName, c.postalCode].filter(Boolean).join(', ');

  return (
    <div>
      <ModuleHero
        eyebrow="After Rated · Share approval"
        title="Approve pack for partners"
        description="Review this pack, then confirm you consent to sharing. Your Micus analyst sends it from Partners and will message you if anything else is needed."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill label={ready ? 'Confirmed' : canConfirm ? 'Action needed' : 'In progress'} tone={ready ? 'green' : canConfirm ? 'amber' : 'slate'} />
            {data.hasRating && !data.locked && (
              <button type="button" disabled={busy !== null} onClick={() => void openPdf()} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
                {busy === 'pdf' && <Spinner className="h-3.5 w-3.5" />} Download PDF
              </button>
            )}
          </div>
        }
      />

      <div
        className="mz-fade-up mz-d1 mt-5 rounded-2xl p-4 sm:p-5"
        style={{ background: ready ? '#ecfdf5' : '#fff5f8', border: `1px solid ${ready ? '#a7f3d0' : '#f9a8d4'}` }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#d6006e]">Your confirmation</p>
            <p className="mt-1 text-base font-black text-[#6b0030]">
              {ready ? 'Sharing approved' : 'Confirm & consent to partner sharing'}
            </p>
            <p className="mt-1 text-sm text-[#6b3a52]">
              {ready
                ? 'Your analyst can now send this pack from Consult. You do not need to track partners yourself.'
                : confirmBlockReason || 'Approve this Ready pack so your analyst can contact funding partners.'}
            </p>
            <div className="mt-3">
              <SignOff
                label="You confirm & consent to sharing"
                at={brief?.customerConfirmedAt}
                by={brief?.customerConfirmedBy}
                pendingText={confirmBlockReason || 'Click Confirm below when you are ready'}
              />
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:min-w-[220px]">
            <button
              type="button"
              disabled={!canConfirm || busy !== null}
              onClick={() => void confirm()}
              className={`${BTN_PRIMARY} w-full justify-center py-3`}
              style={BTN_PRIMARY_STYLE}
            >
              {busy === 'confirm' && <Spinner />}
              {ready ? 'Confirmed' : 'Confirm & consent'}
            </button>
            {confirmBlockReason && !ready && (
              <p className="text-center text-[11px] text-[#b0708f]">{confirmBlockReason}</p>
            )}
          </div>
        </div>
      </div>

      {notice && (
        <div className="mt-4">
          <Notice tone={notice.tone}>{notice.text}</Notice>
        </div>
      )}
      {['INVITED', 'INTAKE'].includes(c.status) && (
        <div className="mt-4">
          <Notice tone="pink">
            The Ready pack opens once your documents are submitted.{' '}
            <Link to="/documents" className="font-bold underline">
              Go to documents
            </Link>
          </Notice>
        </div>
      )}
      {data.locked && data.hasRating && (
        <div className="mt-4">
          <Notice tone="amber">
            Pay the rating fee on Rated to unlock this pack.{' '}
            <Link to="/rating" className="font-bold underline">
              Open Rated
            </Link>
          </Notice>
        </div>
      )}

      <div className="mt-5 grid gap-5 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <Card title="Company at a glance">
            <dl className="grid grid-cols-2 gap-3">
              <Fact label="Legal name" value={c.legalName || c.name} />
              <Fact label="Entity type" value={c.classification?.type.label} />
              <Fact label="Country" value={c.countryCode} />
              <Fact label="Sector" value={c.sector} />
              <Fact label="Funding ask" value={c.fundingAmount} />
              <Fact label="Use of funds" value={c.fundingPurpose} />
            </dl>
            {address && <p className="mt-3 border-t border-[#fce7f3] pt-2 text-xs text-[#6b3a52]">{address}</p>}
          </Card>

          <Card title="Micus rating">
            {data.report ? (
              <div className="space-y-2">
                <StatusPill label={`${data.report.grade} · ${data.report.score}/100`} tone={gradeTone(data.report.score)} />
                {data.report.outlook && <p className="text-xs font-semibold uppercase tracking-wider text-[#b0708f]">{data.report.outlook} outlook</p>}
                <p className="text-xs leading-relaxed text-[#6b3a52]">{data.report.summary}</p>
              </div>
            ) : (
              <p className="text-sm text-[#c084a0]">{data.locked && data.hasRating ? 'Locked until the rating fee is paid.' : 'Not rated yet.'}</p>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card
            title="Ready summary"
            subtitle={brief?.updatedAt ? `Last updated ${formatDateTime(brief.updatedAt)} · Your analyst can refine this in Consult` : 'Drafted from your published rating'}
          >
            {!hasOverview ? (
              <Notice tone="amber">Summary is still being prepared. Check back shortly, or ask your analyst to open Ready in Consult.</Notice>
            ) : (
              <>
                <Block label="Company overview" text={brief?.overview} />
                <Block label="Highlights" text={brief?.highlights} />
                <Block label="Financial summary" text={brief?.financials} />
                <Block label="Risks & mitigants" text={brief?.risks} />
              </>
            )}
          </Card>

          <Card title="Major Concerns" subtitle="From your Micus rating — with AI mitigations partners can act on.">
            {majorConcerns.length === 0 ? (
              <p className="text-sm text-[#c084a0]">No open Major Concerns on the published rating.</p>
            ) : (
              <ul className="space-y-3">
                {majorConcerns.map((item) => (
                  <li key={item.id} className="rounded-2xl px-4 py-3" style={{ background: '#fff1f2', border: '1px solid #fecdd3' }}>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill label="Major Concern" tone="rose" />
                      {item.documentCode && <span className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{item.documentCode}</span>}
                    </div>
                    <p className="mt-2 text-sm font-bold text-[#6b0030]">{item.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-[#6b3a52]">{item.detail}</p>
                    <div className="mt-2 rounded-xl bg-white/80 px-3 py-2" style={{ border: '1px solid #fce7f3' }}>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#d6006e]">AI mitigation</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-[#4a1a30]">{item.mitigation}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Documents partners typically require next" subtitle="Your analyst coordinates these in Consult after you confirm.">
            <ul className="space-y-2.5">
              {partnerDocs.map((doc) => (
                <li key={doc.code} className="flex gap-3 rounded-xl px-3 py-2.5" style={{ background: '#fffafc', border: '1px solid #fce7f3' }}>
                  <StatusPill label={doc.required ? 'Required' : 'Optional'} tone={doc.required ? 'pink' : 'slate'} />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#6b0030]">{doc.title}</p>
                    <p className="mt-0.5 text-xs text-[#b0708f]">{doc.purpose}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
